import { useEffect, useMemo, useRef } from 'react';
import { useCurrentUser } from 'api/hooks/useCurrentUser';
import { useQuickSwitchUsers } from 'api/hooks/useQuickSwitchUsers';
import useKeystoreStorageHelpers from 'utils/helpers/keystoreStorage.helpers';
import {
  clearOrgPinKeys,
  getCachedPinKey,
  isPinKeyNotFoundError,
  pinKeyEntryFromBlob,
  PinKeyCacheEntry,
  removeCachedPinKey,
  setCachedPinKey,
  setPinLoginActiveOnDevice,
} from 'utils/helpers/pinKeyCache.helpers';
import { useFirebaseReadyForUser } from './useFirebaseReadyForUser';
import { usePinLoginEnabled } from './usePinLoginEnabled';
import { usePinOrgId } from './usePinOrgId';

// A download that started before a local change (enrol, remove, re-wrap) must not overwrite it.
const changedSince = (snapshot: PinKeyCacheEntry | undefined, orgId: string, email: string): boolean =>
  JSON.stringify(getCachedPinKey(orgId, email)) !== JSON.stringify(snapshot);

// Login-time sync of the device's pin.key cache for everyone at the location. This is what lets the
// switcher know who has a PIN without a live keystore call per tile.
export const useSyncPinKeyCache = (): void => {
  const { data: user } = useCurrentUser();
  const { quickSwitchUsers } = useQuickSwitchUsers();
  const pinEnabled = usePinLoginEnabled();
  const orgId = usePinOrgId();
  const { constructPinKeyPath, getPinKey } = useKeystoreStorageHelpers();

  const rosterKey = useMemo(
    () => quickSwitchUsers.map(({ userId, email }) => `${userId}:${email}`).join(','),
    [quickSwitchUsers],
  );
  const latest = useRef({ quickSwitchUsers, constructPinKeyPath, getPinKey });
  latest.current = { quickSwitchUsers, constructPinKeyPath, getPinKey };

  const tenantId = user?.tenant?.id;
  const locationId = user?.location?.locationId;
  const firebaseReady = useFirebaseReadyForUser(user?.userId);

  useEffect(() => {
    if (!tenantId || !locationId || !orgId) return;

    if (!pinEnabled) {
      clearOrgPinKeys(orgId);
      setPinLoginActiveOnDevice(false);
      return;
    }
    setPinLoginActiveOnDevice(true);

    if (!firebaseReady) return;

    const { quickSwitchUsers: roster } = latest.current;
    if (!roster.length) return;

    let cancelled = false;

    roster.forEach(async ({ userId, email }) => {
      const existing = getCachedPinKey(orgId, email);
      if (existing?.pendingUpload || existing?.pendingDelete) return;

      const pinKeyPath = latest.current.constructPinKeyPath({ tenantId, locationId, userId });
      try {
        const blob = await latest.current.getPinKey(pinKeyPath);
        if (!cancelled && !changedSince(existing, orgId, email)) {
          setCachedPinKey(orgId, email, pinKeyEntryFromBlob({ userId, pinKeyPath }, blob));
        }
      } catch (error) {
        if (!cancelled && isPinKeyNotFoundError(error) && !changedSince(existing, orgId, email)) {
          removeCachedPinKey(orgId, email);
        }
      }
    });

    return () => {
      cancelled = true;
    };
  }, [tenantId, locationId, orgId, pinEnabled, rosterKey, firebaseReady]);
};
